import './Appointments.scss';

import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  getAllInventory,
  getAppointments,
  getGroomerInventory,
} from 'actions/inventory';
import Button from 'atoms/button';
import toast from 'atoms/toast';
import useWindowDimensions from 'hooks/useWindowDimensions';
import Calendar from 'organisms/Calendar';
import CalendarViewFilter from 'organisms/CalendarViewFilter';
import GroomersDropdown from 'organisms/GroomersDropdown';
import { setAllGroomers, setGroomerUuid } from 'store/reducers/inventory';
import { setUnavailability } from 'store/reducers/singleUnavailability';
import { openDrawer, setSelectedGroomer } from 'store/reducers/ui';
import { getDayFromDate, isInThePast } from 'util';

import Header from './Header';
import { findOpeningHours } from './util';

export default function Appointments() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = useRef();
  const { width } = useWindowDimensions();
  const isMobile = width < 600;
  const {
    isOwner,
    firstName: ownerFirstName,
    links: ownerLinks,
    uuid: ownerUuid,
  } = useSelector((state) => state.user);
  const { groomers } = useSelector((state) => state.groomers);
  const { selectedGroomer } = useSelector((state) => state.ui);
  const { locations } = useSelector((state) => state.provider);
  const { view } = useSelector((state) => state.calendar);
  const { earliestStart, latestEnd } = findOpeningHours(locations[0].hours);
  const { availabilityMerged, unavailable, appointments } = useSelector(
    (state) => state.inventory
  );
  const ownerData = {
    name: ownerFirstName,
    uuid: ownerUuid,
    links: ownerLinks,
  };

  const dateStart = searchParams.get('start');
  const dateEnd = searchParams.get('end');
  const events = [...appointments, ...availabilityMerged, ...unavailable];

  useEffect(() => {
    // Add list of groomers to the inventory reducer.
    // We need this to attach grommer information to each unavailable slot
    dispatch(setAllGroomers(groomers));
  }, []);

  // Fetch appointments & inventory
  useEffect(() => {
    if (dateStart && dateEnd) {
      dispatch(getAppointments({ dateStart, dateEnd }));
      if (selectedGroomer.uuid === 'all') {
        dispatch(getAllInventory({ dateStart, dateEnd }));
      } else {
        dispatch(getGroomerInventory({ dateStart, dateEnd }));
      }
    }
  }, [dateStart, dateEnd, selectedGroomer]);

  // Change calendar view (ex; Monthly)
  useEffect(() => {
    const calendarApi = ref.current.getApi();
    calendarApi.changeView(view.id);
  }, [view]);

  // Change dates in calendar (using arrows)
  const onDateChange = ({ startStr, endStr }) => {
    const dateStart = dayjs(startStr).format('YYYY-MM-DD');
    const dateEnd = dayjs(endStr).format('YYYY-MM-DD');
    navigate(`/appointments?start=${dateStart}&end=${dateEnd}`);
  };

  const onClickEvent = (eventData) => {
    const { event } = eventData;
    const { startStr, endStr, _def } = event;
    const {
      isAvailability,
      isUnavailable,
      eventDate: date,
      uuid,
    } = _def.extendedProps;

    if (isAvailability) {
      return openDayAvailabilityDrawer({ startStr, endStr, date });
    }
    if (isUnavailable) {
      return openUnavailableDrawer({ startStr, endStr, _def });
    }
    openAppointment({ auid: uuid });
  };

  const openAppointment = ({ auid }) => {
    dispatch(
      openDrawer({
        type: 'appointment',
        uuid: auid,
      })
    );
  };

  const openDayAvailabilityDrawer = ({ date, startStr, endStr }) => {
    if (isInThePast(startStr)) {
      return toast({ text: "You can't edit past dates" });
    }
    if (!isOwner) {
      selectGroomer(ownerData);
    }

    setTimeout(() => {
      dispatch(
        openDrawer({
          type: 'availability',
          data: {
            date: date || getDayFromDate(startStr),
            startStr,
            endStr,
          },
          uuid: 'event',
        })
      );
    }, 300);
  };

  const openUnavailableDrawer = ({ startStr, endStr, _def }) => {
    if (isInThePast(startStr)) {
      return toast({ text: "You can't edit past dates" });
    }
    if (!isOwner) {
      selectGroomer(ownerData);
    } else {
      const { uuid, firstName, links } = _def.extendedProps.groomer;
      selectGroomer({
        name: firstName,
        uuid,
        links,
      });
    }
    setTimeout(() => {
      dispatch(
        setUnavailability({
          unavailable: [
            {
              end: endStr,
              start: startStr,
              uuid: _def.extendedProps.uuid,
            },
          ],
          date: startStr.split('T')[0],
        })
      );
      dispatch(
        openDrawer({
          type: 'unavailable',
          data: {
            endStr,
            startStr,
          },
          uuid: null,
        })
      );
    }, 300);
  };

  const openRecurringDrawer = () => {
    if (!isOwner) {
      selectGroomer(ownerData);
    }
    dispatch(openDrawer({ type: 'availability', uuid: 'all' }));
  };

  const selectGroomer = (groomer) => {
    dispatch(setSelectedGroomer(groomer));
    dispatch(setGroomerUuid());
  };

  return (
    <>
      <Helmet title="Appointments | PawCare" />
      <div className="page-container appointments-page">
        <Header />
        <div className="appointments-page-section">
          <div className="pos-relative appointments-page-calendar-container">
            <div className="calendar-filter-container pos-absolute flex-row">
              {isMobile && <GroomersDropdown className="mr-10" />}
              <CalendarViewFilter />
              <Button
                text="Edit weekly availability"
                className="fw-400 ml-10"
                shape="round"
                size="small"
                variant="white-outlined"
                onClick={openRecurringDrawer}
              />
            </div>
            <Calendar
              ref={ref}
              view={view}
              events={events}
              initialDate={dateStart}
              slotMinTime={earliestStart}
              slotMaxTime={latestEnd}
              handleDatesChange={onDateChange}
              onClickBackground={openDayAvailabilityDrawer}
              onClickEvent={onClickEvent}
            />
          </div>
        </div>
      </div>
    </>
  );
}
